import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Popconfirm, Select, Table, Tag } from "antd";
import { useTerritories } from "@/modules/territories/hooks/useTerritories";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import { useDeletePlanting, usePlantingsList, usePlants } from "../hooks/usePlantings";
import { usePlantingsStore } from "../store/plantings.store";
import type { Planting } from "../types";
import { formatDateTime, geometryTypeLabel } from "../utils/format";
import { PlantingFormModal } from "./PlantingFormModal";

interface PlantingsTableProps {
	organizationId?: string;
}

export function PlantingsTable({ organizationId }: PlantingsTableProps) {
	const { page: currentPage, limit: pageSize, setPage, getFilter, setFilters } = useUrlTableState();
	const plantId = getFilter("plantId");
	const territoryId = getFilter("territoryId");

	const openEditModal = usePlantingsStore((s) => s.openEditModal);
	const editingItem = usePlantingsStore((s) => s.editingItem);

	const { data, isLoading } = usePlantingsList({
		organizationId,
		page: currentPage,
		limit: pageSize,
		plantId,
		territoryId,
	});

	const { data: plantsData } = usePlants(organizationId ?? "");
	const { data: territoriesData } = useTerritories({
		organizationId: organizationId ?? "",
		page: 1,
		limit: 100,
	});

	const { mutate: deletePlanting } = useDeletePlanting();

	const items = data?.data?.items ?? [];
	const total = data?.data?.meta?.total ?? 0;
	const plants = plantsData?.data?.items ?? [];
	const territories = territoriesData?.data?.items ?? [];

	return (
		<>
			<TableToolbar
				filters={
					<>
						<Select
							placeholder="O'simlik"
							allowClear
							showSearch
							style={{ width: 180 }}
							value={plantId}
							onChange={(v) => setFilters({ plantId: v ?? null })}
							filterOption={(input, option) =>
								(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
							}
							options={plants.map((p) => ({ value: p.id, label: p.name }))}
						/>
						<Select
							placeholder="Hudud"
							allowClear
							showSearch
							style={{ width: 180 }}
							value={territoryId}
							onChange={(v) => setFilters({ territoryId: v ?? null })}
							filterOption={(input, option) =>
								(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
							}
							options={territories.map((t) => ({ value: t.id, label: t.name }))}
						/>
					</>
				}
			/>
			<Table
				loading={isLoading}
				pagination={tablePagination({
					page: currentPage,
					limit: pageSize,
					total,
					onChange: (page, size) => setPage(page, size ?? pageSize),
				})}
				size="small"
				scroll={{ x: "max-content" }}
				dataSource={items}
				rowKey="id"
				columns={[
					{
						key: "#",
						title: "#",
						width: 60,
						align: "center",
						render: (_, __, index) => formatTableIndex(currentPage, pageSize, index),
					},
					{
						key: "plant",
						title: "O'simlik",
						ellipsis: true,
						render: (_, record: Planting) => record.plantName ?? record.plantId,
					},
					{
						key: "territory",
						title: "Hudud",
						width: 160,
						render: (_, record: Planting) => record.territoryName ?? "—",
					},
					{
						key: "contract",
						title: "Shartnoma",
						width: 140,
						render: (_, record: Planting) => record.contractNumber ?? "—",
					},
					{
						key: "geometry",
						title: "Geometriya",
						width: 110,
						align: "center",
						render: (_, record: Planting) => {
							const isPoint =
								record.geometry.type === "Point" || record.geometry.type === "MultiPoint";
							return (
								<Tag color={isPoint ? "blue" : "green"}>
									{geometryTypeLabel(record.geometry.type)}
								</Tag>
							);
						},
					},
					{
						key: "quantity",
						title: "Soni",
						width: 90,
						align: "right",
						render: (_, record: Planting) => record.quantity ?? "—",
					},
					{
						key: "plantedAt",
						title: "Ekilgan sana",
						width: 150,
						render: (_, record: Planting) => formatDateTime(record.plantedAt),
					},
					{
						key: "actions",
						title: "Amallar",
						width: 80,
						align: "center",
						render: (_, record: Planting) => {
							const menuItems: MenuProps["items"] = [
								{
									key: "edit",
									icon: <EditOutlined />,
									label: "Tahrirlash",
									onClick: () => openEditModal(record),
								},
								{
									key: "delete",
									icon: <DeleteOutlined />,
									danger: true,
									label: (
										<Popconfirm
											title="Ekinni o'chirish"
											description="Ushbu ekinni o'chirishni tasdiqlaysizmi?"
											okText="O'chirish"
											cancelText="Bekor qilish"
											okButtonProps={{ danger: true }}
											onConfirm={() => deletePlanting(record.id)}
										>
											<span>O'chirish</span>
										</Popconfirm>
									),
								},
							];

							return (
								<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
									<Button type="text" icon={<MoreOutlined />} size="small" />
								</Dropdown>
							);
						},
					},
				]}
			/>
			{(organizationId || editingItem) && (
				<PlantingFormModal organizationId={organizationId ?? editingItem?.organizationId ?? ""} />
			)}
		</>
	);
}
